import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Edge, Node, BackgroundVariant } from 'reactflow';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, CircularProgress, Grid2, Divider } from '@mui/material';
import { DataGrid, GridRowParams, GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';
import { LoadingButton } from '@mui/lab';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { IPagination } from '@/src/types/common.interface';
import DirectiveGraph from '@/src/components/common/graph-components-assets/DirectiveGraph';
import AppConfig from '@/src/config/AppConfig';

import { RootState, store } from '@/src/datastore/store';
import {
  IApiClientResponse,
  IAttributeOptions,
  ILooseObject,
  INPUT_FIELD_TYPE_MAP,
  INPUT_TYPES_ENUM,
  ListingTableDataStateType,
  UI_TYPE_INPUT_TYPE_MAP,
  DYNAMIC_INPUT_TYPES_ENUM
} from '@/src/types/common.interface';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import AppMessages from '@/src/config/message_config/AppMessages';
import SchemaValidations from '@/src/utils/SchemaValidations';
import lightTheme from '@/src/styles/themes/lightTheme';
import AllUsersService from '@/src/services/all-users/allUsersService';
import AllRolesService from '@/src/services/all-roles/allRolesService';
import DynamicBreadCrumbs from '@/src/components/common/DynamicBreadCrumbs';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import DynamicInput from '@/src/components/common/DynamicInput';
import ConfirmDialog from '@/src/components/common/ConfirmDialog';
import GraphTableSwitcher from '@/src/components/common/GraphTableSwitcher';
import UserMetaDataRow from '@/src/components/UserMetaDataRow';

const UserDetails = () => {
  const appConfigData = useSelector((state: RootState) => state?.appConfigData);

  const [userDetails, setUserDetails] = useState<ILooseObject>({});
  const [defaultUsersValues, setDefaultUsersValues] = useState<ILooseObject>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDeleteUser, setIsLoadingDeleteUser] = useState<boolean>(false);
  const [inputOptions, setInputOptions] = useState<ILooseObject>({}); // contains all the options
  const [isOptionsLoading, setIsOptionsLoading] = useState<ILooseObject>({}); // contains options loading status
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState<boolean>(false);
  const [userUiMode, setUserUiMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const [applicationUiMode, setApplicationUiMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [roleSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [userAssignedRoleTable, setUserAssignedRoleTable] = useState<ListingTableDataStateType>({
    rows: [],
    columns: []
  });
  const [userAssignedRawTable, setUserAssignedRawTable] = useState<ListingTableDataStateType>({
    rows: [],
    columns: []
  });
  const [userDerivedRoleTable, setUserDerivedRoleTable] = useState<ListingTableDataStateType>({
    rows: [],
    columns: []
  });
  const [useDerivedRawTable, setUserDerivedRawTable] = useState<ListingTableDataStateType>({
    rows: [],
    columns: []
  });
  const [version, setVersion] = useState<number>(0);
  const [initialRoles, setInitialRoles] = useState<string[]>([]);
  const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false);
  const [commonAppKeys, setCommonAppKeys] = useState<string[]>([]);
  const [unSelectableRows, setUnSelectableRows] = useState<ILooseObject>([]);
  const [parentChildRelations, setParentChildRelations] = useState<ILooseObject>({});
  const [rolesNotInEditor, setRolesNotInEditor] = useState<ILooseObject>({});
  const [isCachedManagerOptions, setIsCachedManagerOptions] = useState<boolean>(false);
  const [tabSelectionValue, setTabSelectionValue] = useState<number | null>(
    appConfigData?.selectedTabValue ?? 1
  );
  const [applicationOptions, setApplicationOptions] = useState<IAttributeOptions[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<string>('');
  const [graphData, setGraphData] = useState<{ nodes: Node[]; edges: Edge[] }>({
    nodes: [],
    edges: []
  });
  const [rawGraphData, setRawGraphData] = useState<{ nodes: Node[]; edges: Edge[] }>({
    nodes: [],
    edges: []
  });
  const [initialAssignedGraphRoleIds, setInitialGraphRoleIds] = useState<string[]>([]);
  const [currentAssignedGraphRoleIds, setCurrentAssignedGraphRoleIds] = useState<string[]>([]);
  const [isGraphLoading, setIsGraphLoading] = useState<boolean>(false);
  const [isLoadingGraphRoleSubmission, setIsLoadingGraphRoleSubmission] = useState<boolean>(false);
  const [userAssignedRoleNodesGraph, setUserAssignedRoleNodesGraph] = useState<string[]>([]);
  const [userAttributesDefinition, setUserAttributesDefinition] = useState<IAttributeDef[]>([]);
  const [isUserAttributesDefinitionLoading, setIsUserAttributesDefinitionLoading] =
    useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const userKey = searchParams.get('userKey');
  const organizationKey = searchParams.get('orgKey');
  const schema = SchemaValidations.AddUsers(AttributesConfig.USER_DETAILS);

  const defaultMetaRow = () => ({
    id: uuidv4(),
    selectedAttributeName: '',
    selectedAttributeId: null,
    attributeValue: ''
  });

  const formMethods = useForm({
    defaultValues: {
      ...defaultUsersValues,
      metadata: []
    },
    resolver: yupResolver(schema, { abortEarly: false }),
    mode: 'onBlur'
  });

  const {
    control: controlUserDetails,
    handleSubmit: handleSubmitUserDetails,
    formState: {
      errors: errorsUserDetails,
      isSubmitting: isSubmittingUserDetails,
      isDirty: isDirtyUserDetails,
      dirtyFields: dirtyFieldsUserDetails
    },
    getValues: getValuesUserDetails,
    clearErrors: clearErrorsUserDetails,
    reset: resetUserDetails,
    setValue: setValueUserDetails
  } = formMethods;

  // Use useFieldArray for metadata
  const {
    fields: metaFields,
    append: appendMeta,
    remove: removeMeta
  } = useFieldArray({
    control: controlUserDetails,
    name: 'metadata'
  });

  // applications and role form
  const {
    control: controlApplications,
    handleSubmit: handleSubmitApplications,
    formState: { isDirty: isDirtyApplications, isSubmitting: isSubmittingApplications },
    reset: resetApplications
  } = useForm({
    defaultValues: { roles: [...roleSelectionModel] },
    mode: 'onBlur'
  });

  const { organization_keys: selectedOrgKey } = getValuesUserDetails();
  const meApiData = useSelector((state: RootState) => state?.userDetails);
  const userOrgPermissions: ILooseObject = meApiData?.OptionsAndPermissions?.orgPermissions;

  const appKeysWithPermissions: ILooseObject = meApiData?.OptionsAndPermissions?.appPermissions;

  const permissionsNeededForEdit: string[] = ['user_add_role', 'all_role_view'];

  const validAppKeys: string[] = Object.keys(appKeysWithPermissions ?? {})?.filter(
    (appKey: string) => {
      return permissionsNeededForEdit?.every((permission: string) =>
        appKeysWithPermissions[appKey].includes(permission)
      );
    }
  );

  // requester org with app keys
  const requesterOrgWithAppKeys: ILooseObject = meApiData?.organizations?.reduce(
    (acc: ILooseObject, organization: ILooseObject) => {
      if (organization?.organization_key) {
        acc[organization.organization_key] = organization?.applications
          ?.map((application: ILooseObject) => application?.application_key ?? '-')
          ?.filter((app: string) => validAppKeys?.includes(app));
      }
      return acc;
    },
    {}
  );

  const getUserPermissionsUserPage = () => {
    if (organizationKey) {
      return organizationKey?.split(',')?.reduce((acc: string[], key: string) => {
        return Array.from(new Set([...acc, ...(userOrgPermissions?.[key] ?? [])]));
      }, []);
    }
    return [];
  };

  const isAuthorized: boolean =
    getUserPermissionsUserPage()?.includes('user_view') ||
    getUserPermissionsUserPage()?.includes('all_role_view');

  const processDefaultUserData = (userData: ILooseObject, userAttributes: IAttributeDef[]) => {
    const defaultUserValuesCollection = userAttributes?.reduce(
      (collectedValues: ILooseObject, currentAttribute: IAttributeDef) => {
        if (currentAttribute?.name === 'organization_keys') {
          collectedValues.organization_keys = userData?.organizations?.[0]?.organization_key;
          //TODO: When organization field will be multi select support in future then we have to change here also
        } else if (currentAttribute?.name === 'manager_user_key') {
          collectedValues.manager_user_key = userData?.manager?.user_key;
        } else {
          collectedValues[currentAttribute?.name] = userData?.[currentAttribute?.name];
        }

        return collectedValues;
      },
      {}
    );

    // Process metadata
    const metadata = userData?.metadata
      ? Object.entries(userData.metadata).map(([key, value]) => ({
          id: uuidv4(),
          selectedAttributeName: key,
          selectedAttributeId: null,
          attributeValue: Array.isArray(value) ? value : value
        }))
      : [];

    return {
      ...defaultUserValuesCollection,
      metadata
    };
  };

  /**
   * Recursively finds all parent node IDs for a given node in a graph.
   * @param nodeId - The node ID to start from.
   * @param nodeEdges - Map of node IDs to their parent/child relationships.
   * @param visited - Set of already visited node IDs to prevent cycles.
   * @returns Array of all parent node IDs (direct and indirect).
   */
  const findAllParents = (
    nodeId: string,
    nodeEdges: Map<string, { parents: string[]; children: string[] }>,
    visited = new Set<string>()
  ): string[] => {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);

    const node = nodeEdges.get(nodeId);
    if (!node) return [];

    const directParents = node.parents;
    const allParents = new Set<string>();
    if (directParents?.length > 0) {
      directParents?.forEach((parent: string) => {
        allParents.add(parent);
      });
    }

    directParents.forEach((parentId) => {
      const grandParents = findAllParents(parentId, nodeEdges, visited);
      grandParents?.forEach((grandParent: string) => {
        allParents.add(grandParent);
      });
    });

    return Array.from(allParents);
  };

  /**
   * Recursively finds all child node IDs for a given node in a graph.
   * @param nodeId - The starting node ID.
   * @param nodeEdges - Map of node IDs to their parent/child relationships.
   * @param visited - Set of visited node IDs to prevent cycles.
   * @returns Array of all child node IDs (direct and indirect).
   */
  const findAllChildren = (
    nodeId: string,
    nodeEdges: Map<string, { parents: string[]; children: string[] }>,
    visited = new Set<string>()
  ): string[] => {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);

    const node = nodeEdges.get(nodeId);
    if (!node) return [];

    const directChildren = node.children;
    const allChildren = new Set<string>();

    if (directChildren?.length > 0) {
      directChildren?.forEach((child: string) => {
        allChildren.add(child);
      });
    }

    directChildren.forEach((childId) => {
      const grandChildren = findAllChildren(childId, nodeEdges, visited);
      grandChildren?.forEach((grandChild: string) => {
        allChildren.add(grandChild);
      });
    });

    return Array.from(allChildren);
  };

  /**
   * Builds parent-child relationships for nodes based on edges.
   * @param allNodes Array of all nodes.
   * @param allEdges Array of all edges.
   * @param nodeEdgesRelationMapRef Map of node IDs to their parents and children.
   */
  const buildParentChildRelationOfNodes = (
    allNodes: Node[],
    allEdges: Edge[],
    nodeEdgesRelationMapRef: Map<string, { parents: string[]; children: string[] }>
  ): void => {
    allNodes.forEach((node) => {
      nodeEdgesRelationMapRef.set(node.id, { parents: [], children: [] });
    });

    // Build the parent-child relationships from edges
    allEdges?.forEach((edge) => {
      const parentNode = nodeEdgesRelationMapRef.get(edge.source);
      const childNode = nodeEdgesRelationMapRef.get(edge.target);

      if (parentNode && childNode) {
        parentNode.children.push(edge.target);
        childNode.parents.push(edge.source);
      }
    });
  };

  const getUserDetails = async (userKey: string) => {
    try {
      setIsLoading(true);
      const results = await AllUsersService.getUserDetailByUserKey({ userKey });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setUserDetails({});
        setUserAssignedRoleTable({
          rows: [],
          columns: []
        });
        setUserDerivedRoleTable({
          rows: [],
          columns: []
        });
        setIsEditEnabled(false);
      } else if (!results.error.code) {
        // user details
        setUserDetails(results?.data);
        setVersion(results?.data?.version ?? 0);
        const processedUserData = processDefaultUserData(
          results?.data,
          AttributesConfig.USER_DETAILS
        );
        setDefaultUsersValues(processedUserData);
        resetUserDetails(processedUserData);
        const managerOptions = [
          {
            label: results?.data?.manager?.name ?? '-',
            value: results?.data?.manager?.user_key ?? '-',
            details: results?.data?.manager?.email_id ?? '-'
          }
        ];
        setInputOptions((prev) => ({
          ...prev,
          manager_user_key: managerOptions
        }));

        // role tagging
        // store org keys with app keys
        const userOrgKeysWithAppKeys: ILooseObject = results?.data?.organizations?.reduce(
          (acc: ILooseObject, organization: ILooseObject) => ({
            ...acc,
            ...(organization?.organization_key && {
              [organization.organization_key]: organization?.applications?.map(
                (application: ILooseObject) => application?.application_key ?? '-'
              )
            })
          }),
          {}
        );

        const sortedRows: ILooseObject[] =
          results?.data?.table?.rows?.sort((a: ILooseObject, b: ILooseObject) => {
            const labelA = a?.application_label ?? a?.organization_label ?? '';
            const labelB = b?.application_label ?? b?.organization_label ?? '';

            return labelA > labelB ? 1 : labelA < labelB ? -1 : 0;
          }) ?? [];
        const categorizedTableRows = sortedRows?.reduce(
          (
            collection: { userAssignedRows: ILooseObject[]; userDerivedRows: ILooseObject[] },
            currentRow: ILooseObject
          ) => {
            if (currentRow?.parent_roles && currentRow?.parent_roles?.length > 0) {
              collection.userDerivedRows.push(currentRow);
            } else {
              collection.userAssignedRows.push(currentRow);
            }
            return collection;
          },
          { userAssignedRows: [], userDerivedRows: [] }
        );

        const userAssignedRoleNodesGraph = categorizedTableRows?.userAssignedRows?.map(
          (userAssignedRole: ILooseObject) => {
            return userAssignedRole?.role_key;
          }
        );

        const userAssignedTableData = {
          columns: results?.data?.table?.columns?.filter(
            (column: ILooseObject) => column?.field !== 'parent_roles'
          ),
          rows: categorizedTableRows?.userAssignedRows
        };
        const userDerivedTableData = {
          columns: results?.data?.table?.columns?.filter(
            (column: ILooseObject) => column?.field !== 'organization_label'
          ),
          rows: categorizedTableRows?.userDerivedRows
        };
        setUserAssignedRoleTable(userAssignedTableData);
        setUserAssignedRawTable(userAssignedTableData);
        setUserAssignedRoleNodesGraph(userAssignedRoleNodesGraph);
        setUserDerivedRoleTable(userDerivedTableData);
        setUserDerivedRawTable(userDerivedTableData);

        const childrenRoles: string[] = [];
        const selectedRoleKeys: string[] = [];
        sortedRows?.forEach((role: ILooseObject) => {
          if (role?.role_key) {
            selectedRoleKeys.push(role?.role_key);
          }
          if (role?.parent_roles) {
            childrenRoles.push(role?.role_key);
          }
        });
        setInitialRoles(selectedRoleKeys?.sort());
        const commonApps: Set<string> = new Set(); // Ensure Set contains strings
        for (const orgKey in userOrgKeysWithAppKeys) {
          if (requesterOrgWithAppKeys[orgKey]) {
            // both belongs to same org
            const requesterApps = new Set<string>(requesterOrgWithAppKeys[orgKey]);

            for (const appKey of requesterApps) {
              commonApps.add(appKey);
            }
          }
        }
        const commonKeys = [...commonApps]; // Convert Set to array
        setCommonAppKeys(commonKeys);
        setIsEditEnabled(!!validAppKeys?.length && !!commonKeys?.length);
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
      setUserDetails({});
      setIsEditEnabled(false);
      setUserAssignedRoleTable({ columns: [], rows: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const getAllUserTypeList = async (): Promise<IAttributeOptions[]> => {
    try {
      const results = await AllUsersService.getAllUserTypeList();

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        return [];
      } else if (!results.error.code) {
        const convertedUserTypeOption = results?.data?.map((user: ILooseObject) => ({
          label: user?.ui_label ?? '-',
          value: user?.name ?? '-'
        }));
        return convertedUserTypeOption;
      }
      return [];
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
      return [];
    }
  };

  const getAllUserAttributesDefinition = async () => {
    try {
      setIsUserAttributesDefinitionLoading(true);
      const results = await AllUsersService.getUserAttributesDefinition();
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setUserAttributesDefinition([]);
      } else if (!results.error.code) {
        setUserAttributesDefinition(
          Array.isArray(results?.data) ? (results.data as IAttributeDef[]) : []
        );
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsUserAttributesDefinitionLoading(false);
    }
  };

  const getAllRolesByApplications = async (applicationKeys: string) => {
    try {
      setIsLoadingRoles(true);
      const results = await AllUsersService.getRolesListByApplications({
        applicationKeys
      });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setUserAssignedRoleTable(userAssignedRawTable);
        setApplicationUiMode('VIEW');
      } else if (!results.error.code) {
        // Separation of roles
        const userAssignableRoles =
          results?.rows?.filter(
            (role: ILooseObject) =>
              role?.application_key && applicationKeys?.includes(role.application_key)
          ) || [];

        const sortedRows = userAssignableRoles?.sort((a: ILooseObject, b: ILooseObject) => {
          const labelA = a?.application_label ?? a?.organization_label ?? '';
          const labelB = b?.application_label ?? b?.organization_label ?? '';

          return labelA > labelB ? 1 : labelA < labelB ? -1 : 0;
        });

        const parentChildKeysRelation = sortedRows?.reduce(
          (relationCollection: ILooseObject, currentRow: ILooseObject) => {
            const isParent = currentRow?.is_parent;
            const isChild = currentRow?.parent_role_key;
            if (isParent) {
              const childCollection =
                currentRow?.child_roles?.map((childRole: ILooseObject) => childRole?.role_key) ??
                [];
              const collection: ILooseObject = {
                isParent: true,
                children: childCollection
              };
              //Checking in the parent role can be a child role also
              if (isChild) {
                collection.parents = [...(currentRow?.parent_role_key ?? [])];
              }
              relationCollection[currentRow?.role_key] = collection;
            } else if (isChild) {
              relationCollection[currentRow?.role_key] = {
                isParent: false,
                parents: [...(currentRow?.parent_role_key ?? [])]
              };
            }
            return relationCollection;
          },
          {}
        );

        setParentChildRelations(parentChildKeysRelation);
        // Creating maps of roles with their keys
        const sortedRoleMappingWithRoleKey = sortedRows?.reduce(
          (collection: ILooseObject, row: ILooseObject) => {
            collection[row?.role_key] = row;
            return collection;
          },
          {}
        );
        const CollectionRolesNotInEditor: ILooseObject = {};
        userAssignedRawTable?.rows?.forEach((row: ILooseObject) => {
          // need to add those roles whose are not exist in sortedRows with disabled flag
          if (!sortedRoleMappingWithRoleKey?.[row?.role_key]) {
            const updatedRow: ILooseObject = { ...row, disabled: true }; // adding disabled flag, so user can't change it
            sortedRows.push(updatedRow);
            CollectionRolesNotInEditor[row?.role_key] = updatedRow;
          }
        });

        setUserAssignedRoleTable({
          columns: results?.columns,
          rows: sortedRows
        });
        setRolesNotInEditor(CollectionRolesNotInEditor);

        // Collect the collection of most parent keys from initialRoles with the help of parentChildKeysRelation
        const collectTopMostKeys = (collection: string[], relations: ILooseObject): string[] => {
          const collected = new Set(collection); // Initial collection
          const result: Set<string> = new Set();

          for (const key of collection) {
            const relation = relations[key];

            // Case: No relation → add as-is
            if (!relation) {
              result.add(key);
              continue;
            }

            // Check parents
            if (relation.parents && relation.parents.length > 0) {
              const hasParentInCollection = relation.parents.some((parent: string) =>
                collected.has(parent)
              );
              if (hasParentInCollection) {
                continue; // Skip this key because parent exists
              } else {
                result.add(key);
              }
            } else {
              result.add(key); // No parents, safe to add
            }

            // Check children
            if (relation.children && relation.children.length > 0) {
              for (const child of relation.children) {
                if (collected.has(child)) {
                  result.delete(child); // Remove child if present
                }
              }
            }
          }
          return Array.from(result);
        };

        const topMostKeys: string[] = collectTopMostKeys(initialRoles, parentChildKeysRelation);

        setRowSelectionModel(topMostKeys?.sort() ?? []);
        resetApplications({
          roles: topMostKeys?.sort() ?? []
        });
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const getAllUsers = useCallback(async () => {
    try {
      setIsOptionsLoading((prev) => ({
        ...prev,
        manager_user_key: true
      }));
      const results = await AllUsersService.getAllUsersByOrganization({
        filters: {
          organization_keys: selectedOrgKey,
          user_type: 'all'
        }
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        const convertedUserOption = results?.data?.map((user: ILooseObject) => ({
          label: user?.name ?? '-',
          value: user?.user_key ?? '-',
          details: user?.email_id
        }));
        setInputOptions((prev) => ({
          ...prev,
          manager_user_key: convertedUserOption
        }));
        setIsCachedManagerOptions(true);
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsOptionsLoading((prev) => ({
        ...prev,
        manager_user_key: false
      }));
    }
  }, [selectedOrgKey]);

  const getAllGraphRoles = async (paginationData: IPagination, applicationKeys: string[]) => {
    try {
      setIsGraphLoading(true);
      const results = await AllRolesService.getAllGraphRolesList({
        pagination: paginationData,
        filters: {
          application_key: applicationKeys
        }
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setGraphData({
          nodes: [],
          edges: []
        });
        setRawGraphData({
          nodes: [],
          edges: []
        });
      } else if (!results.error.code) {
        const { nodes, edges } = results?.data ?? { nodes: [], edges: [] };

        const nodeEdgesRelation = new Map<string, { parents: string[]; children: string[] }>(); // Reference of node and edges relation
        buildParentChildRelationOfNodes(nodes, edges, nodeEdgesRelation);

        // Find all parents and children of the clicked node
        const listOfAllParentIds: Set<string> = new Set();
        const listOfAllChildIds: Set<string> = new Set();

        // First, build the complete relationship map for all nodes
        nodes.forEach((node: Node) => {
          const allParentIds = findAllParents(node.id, nodeEdgesRelation);
          const allChildIds = findAllChildren(node.id, nodeEdgesRelation);

          // Store these relationships in the node's data for later use
          node.data = {
            ...node.data,
            allParentIds,
            allChildIds
          };
        });

        // Then, for assigned nodes, add their parents and children to the sets
        nodes.forEach((node: Node) => {
          const isAssigned = userAssignedRoleNodesGraph.includes(node.id);
          if (isAssigned) {
            // Add all parents of this node to the parent set
            node.data.allParentIds?.forEach((parentId: string) => {
              listOfAllParentIds.add(parentId);
            });

            // Add all children of this node to the child set
            node.data.allChildIds?.forEach((childId: string) => {
              listOfAllChildIds.add(childId);
            });
          }
        });

        const formattedNodes = nodes.map((node: Node) => {
          const isAssigned = userAssignedRoleNodesGraph.includes(node.id);
          return {
            ...node,
            position: { x: 0, y: 0 },
            type: 'custom',
            data: {
              ...node.data,
              label: node?.data?.role?.ui_label,
              isHovered: false,
              isUnAssigned: false,
              isAdded: isAssigned,
              isParentDisabled: Array.from(listOfAllParentIds).includes(node?.id),
              isChildrenDisabled: Array.from(listOfAllChildIds).includes(node?.id),
              isDisabled: true
            }
          };
        });
        const formattedEdges = edges.map((edge: Edge) => ({
          ...edge,
          animated: false
        }));
        const assignedRolesIds = (
          formattedNodes?.reduce((acc: string[], updatedNode: Node) => {
            if (updatedNode?.data?.isAdded) {
              acc.push(updatedNode?.data?.role?.role_key);
            }
            return acc;
          }, []) ?? []
        )?.sort();

        setInitialGraphRoleIds(assignedRolesIds);
        setCurrentAssignedGraphRoleIds(assignedRolesIds);
        setGraphData({
          nodes: formattedNodes,
          edges: formattedEdges
        });
        setRawGraphData({
          nodes: formattedNodes,
          edges: formattedEdges
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsGraphLoading(false);
    }
  };

  const handleResetUserDetails = () => {
    resetUserDetails();
    setUserUiMode('VIEW');
  };

  const handleResetApplications = () => {
    if (tabSelectionValue === 1) {
      setGraphData(rawGraphData);
      setCurrentAssignedGraphRoleIds(initialAssignedGraphRoleIds);
    } else {
      resetApplications({ roles: (roleSelectionModel as string[]) ?? [] });
      filterRolesByApplication(selectedApplication);
    }
    setApplicationUiMode('VIEW');
  };

  const handleDeleteUser = async (userKey: string) => {
    try {
      setIsLoadingDeleteUser(true);
      const results = await AllUsersService.deleteUserByUserKey({
        userKey
      });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.delete_user, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        navigate('/users');
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingDeleteUser(false);
    }
  };

  const handleEditUserDetails = () => {
    setUserUiMode('EDIT');
    if (!isCachedManagerOptions) {
      getAllUsers();
    }
  };

  const processPayloadData = (data: ILooseObject) => {
    const currentData = { ...data };
    const updatedMetaData = currentData?.metadata?.reduce(
      (collection: ILooseObject, metaValue: ILooseObject) => {
        if (metaValue?.selectedAttributeName) {
          collection[metaValue?.selectedAttributeName] = metaValue?.attributeValue;
        }
        return collection;
      },
      {}
    );
    currentData.metadata = updatedMetaData;
    return currentData;
  };

  const handleUpdateUserDetails = async (data: ILooseObject) => {
    const version = userDetails?.version ?? 0;
    try {
      if (!userKey) {
        throw new Error('User key is not defined');
      }
      const processedData = processPayloadData(data);
      const updatedPayload = Object.keys(dirtyFieldsUserDetails)?.reduce(
        (acc: ILooseObject, key: string) => {
          if (processedData?.[key]) {
            acc[key] = processedData?.[key];
          }
          return acc;
        },
        {}
      );

      const results = await AllUsersService.updateUserDetailsByUserKey({
        filters: { v: version },
        payload: updatedPayload,
        userKey
      });
      if (results.error.message) {
        throw new Error(results.error.message);
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.user_updated, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
        setUserUiMode('VIEW');
        setUserDetails(results?.data);
        setVersion(results?.data?.version ?? 0);
        const processedUserData = processDefaultUserData(
          results?.data,
          AttributesConfig.USER_DETAILS
        );
        setDefaultUsersValues(processedUserData);
        resetUserDetails(processedUserData);
      }
    } catch (error: unknown) {
      enqueueSnackbar((error as Error)?.message, {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
  };

  const handleChangeTab = (value: number) => {
    setTabSelectionValue(value);
  };

  const handleEditRoles = () => {
    setApplicationUiMode('EDIT');
    if (selectedApplication) {
      if (tabSelectionValue === 0) {
        getAllRolesByApplications(selectedApplication);
      } else {
        const updatedGraphNodes = graphData?.nodes?.map((node: Node) => {
          return { ...node, data: { ...node?.data, isDisabled: false } };
        });
        setGraphData((pre) => ({
          ...pre,
          nodes: updatedGraphNodes
        }));
      }
    }
  };

  const handleNodeStateChange = (clickedNode: Node, allNode: Node[]): Node[] => {
    const isChildrenDisabled = clickedNode?.data?.isChildrenDisabled;
    const isParentDisabled = clickedNode?.data?.isParentDisabled;
    // If clicked node is already disabled or parent or children are disabled then return all nodes
    if (isChildrenDisabled || isParentDisabled) {
      return allNode;
    }

    const parentNodeIds = clickedNode?.data?.allParentIds;
    const childNodeIds = clickedNode?.data?.allChildIds;

    const mapAllNodes = allNode?.reduce((acc: ILooseObject, currentNode: Node) => {
      acc[currentNode?.id] = currentNode;
      return acc;
    }, {});
    const isAdded: boolean = !clickedNode?.data?.isAdded;

    const updatedClickedNode: Node = {
      ...mapAllNodes?.[clickedNode?.id],
      data: {
        ...mapAllNodes?.[clickedNode?.id]?.data,
        isAdded: isAdded,
        isParentDisabled: false,
        isChildrenDisabled: false
      }
    };

    // updating all node with current node changes will be effect parent and children, needed for the calculation of parent and child node
    const updatedMapAllNodes: ILooseObject = {
      ...mapAllNodes,
      [clickedNode?.id]: updatedClickedNode
    };

    const updatedParentNodes = (updatedParentCollection: ILooseObject = {}): ILooseObject => {
      return parentNodeIds?.reduce((acc: ILooseObject, parentNodeId: string) => {
        const currentParentNode: Node = updatedMapAllNodes?.[parentNodeId];
        let isAnyChildAssignedOrParentDisabled: boolean = false;
        currentParentNode?.data?.allChildIds?.forEach((childId: string) => {
          // Now watching all the child properties like isAdded and isParentDisabled if anyone get true then this have effected by any child node
          // after first updated parent node need to read the data from updated node `updatedParentCollection`
          const childNode: Node =
            acc[childId] ?? updatedParentCollection?.[childId] ?? updatedMapAllNodes?.[childId];
          const isAddedChild = childNode?.data?.isAdded;
          const isParentDisabled = childNode?.data?.isParentDisabled;
          if (isAddedChild || isParentDisabled) {
            isAnyChildAssignedOrParentDisabled = true;
          }
        });
        // changing parent as isParentDisabled false, If other children not isAssigned or isParentDisabled as true
        // now iterating all children of this parent node
        const updatedNode: Node = {
          ...currentParentNode,
          data: {
            ...currentParentNode?.data,
            isParentDisabled: isAnyChildAssignedOrParentDisabled
          }
        };
        acc[parentNodeId] = updatedNode;

        return acc;
      }, {});
    };

    const updateChildrenNodes = (collectionUpdatedChildren: ILooseObject = {}): ILooseObject => {
      return childNodeIds?.reduce((acc: ILooseObject, currentChildId: string) => {
        const currentChildNode: Node = updatedMapAllNodes?.[currentChildId];

        let isAnyParentAssignedOrChildDisabled: boolean = false;

        // Check all parents of this child node
        currentChildNode?.data?.allParentIds?.forEach((parentId: string) => {
          // Get parent node from updated collection or original map
          const parentNode: Node =
            acc?.[parentId] ??
            collectionUpdatedChildren?.[parentId] ??
            updatedMapAllNodes?.[parentId];
          const isAddedParent = parentNode?.data?.isAdded;
          const isChildDisabled = parentNode?.data?.isChildrenDisabled;

          if (isAddedParent || isChildDisabled) {
            isAnyParentAssignedOrChildDisabled = true;
          }
        });

        const updatedCurrentChildNode: Node = {
          ...currentChildNode,
          data: {
            ...currentChildNode?.data,
            isChildrenDisabled: isAnyParentAssignedOrChildDisabled || isAdded
          }
        };
        acc[currentChildId] = updatedCurrentChildNode;
        return acc;
      }, {});
    };
    //TODO: need to reduce the number of times we are updating the parent and children nodes
    const initialUpdatedParentNodes: ILooseObject = updatedParentNodes();
    const reUpdatedParent: ILooseObject = updatedParentNodes(initialUpdatedParentNodes);

    const initialUpdatedChildrenNodes: ILooseObject = updateChildrenNodes();
    const reUpdatedChildren0: ILooseObject = updateChildrenNodes(initialUpdatedChildrenNodes);
    const reUpdatedChildren1: ILooseObject = updateChildrenNodes(reUpdatedChildren0);
    const reUpdatedChildren2: ILooseObject = updateChildrenNodes(reUpdatedChildren1);
    const reUpdatedChildren3: ILooseObject = updateChildrenNodes(reUpdatedChildren2);

    return Object.values(updatedMapAllNodes)?.map((node: Node) => {
      return reUpdatedParent?.[node?.id] || reUpdatedChildren3?.[node?.id] || node;
    });
  };

  const handleNodeClick = (_: React.MouseEvent, node: Node) => {
    const updatedNodes = handleNodeStateChange(node, graphData.nodes);

    const newUpdatedRolesIds = (
      updatedNodes?.reduce((acc: string[], updatedNode: Node) => {
        if (updatedNode?.data?.isAdded) {
          acc.push(updatedNode?.data?.role?.role_key);
        }
        return acc;
      }, []) ?? []
    )?.sort();

    setCurrentAssignedGraphRoleIds(newUpdatedRolesIds);
    setGraphData((prev) => ({
      ...prev,
      nodes: updatedNodes
    }));
  };

  const handleSubmitRoles = async (data: ILooseObject) => {
    try {
      // Collecting other roles which are not existing in the selected applications,
      // Because if we haven't added inside the payload then those roles will be removed from this user's account
      setIsLoadingGraphRoleSubmission(true);
      const preAssignedRolesFromOtherApps: string[] = userAssignedRoleNodesGraph;
      const oldOtherAppAssignedRole: string[] = userAssignedRawTable?.rows?.reduce(
        (acc: string[], row: ILooseObject) => {
          if (
            row?.application_key &&
            row?.application_key !== selectedApplication &&
            preAssignedRolesFromOtherApps?.includes(row?.role_key)
          ) {
            acc.push(row?.role_key);
          }
          return acc;
        },
        []
      );

      // Filter out roles that are not in the editor
      const updatedRoles = data?.roles?.filter((roleKey: string) => {
        return !rolesNotInEditor?.[roleKey];
      });
      const payload = {
        roleKeys: [...(updatedRoles ?? []), ...(oldOtherAppAssignedRole ?? [])],
        userKey: userKey ?? '',
        version: version ?? 0
      };

      const results: IApiClientResponse = await AllRolesService.updateRoles(payload);

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.roles_updated, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
        if (userKey) {
          getUserDetails(userKey);
        }
        setApplicationUiMode('VIEW');
        setInitialRoles([]);
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingGraphRoleSubmission(false);
    }
  };

  const collectionDisabledRows = useMemo(() => {
    return Array.from(
      roleSelectionModel?.reduce((collection: Set<string>, roleKey: GridRowId) => {
        const isParent = parentChildRelations?.[roleKey]?.isParent;
        if (isParent) {
          parentChildRelations?.[roleKey]?.children?.forEach((children: string) => {
            collection.add(children);
          });
          if (parentChildRelations?.[roleKey]?.parents) {
            parentChildRelations?.[roleKey]?.parents?.forEach((parent: string) => {
              collection.add(parent);
            });
          }
        } else if (isParent === false) {
          parentChildRelations?.[roleKey]?.parents?.forEach((parent: string) => {
            collection.add(parent);
          });
        }
        return collection;
      }, new Set())
    );
  }, [parentChildRelations, roleSelectionModel]);

  const handleApplicationChanged = (applicationKey: string) => {
    setSelectedApplication(applicationKey);
    filterRolesByApplication(applicationKey);
  };

  const filterRolesByApplication = (applicationKey: string) => {
    if (!applicationKey) {
      // If no application selected, show all roles
      setUserAssignedRoleTable(userAssignedRawTable);
      setUserDerivedRoleTable(useDerivedRawTable);
      return;
    }

    // Filter assigned roles
    const filteredAssignedRoles = userAssignedRawTable.rows.filter(
      (role: ILooseObject) => role.application_key === applicationKey
    );
    setUserAssignedRoleTable({
      ...userAssignedRawTable,
      rows: filteredAssignedRoles
    });

    // Filter derived roles
    const filteredDerivedRoles = useDerivedRawTable.rows.filter(
      (role: ILooseObject) => role.application_key === applicationKey
    );
    setUserDerivedRoleTable({
      ...useDerivedRawTable,
      rows: filteredDerivedRoles
    });
  };

  useEffect(() => {
    // Update initial filtering when user details are loaded
    if (selectedApplication) {
      filterRolesByApplication(selectedApplication);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAssignedRawTable, useDerivedRawTable, selectedApplication]);

  useEffect(() => {
    if (getUserPermissionsUserPage()?.includes('user_view') && userKey) {
      getUserDetails(userKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  useEffect(() => {
    // Load user attributes definition when user details are loaded (for displaying existing metadata)
    if (userDetails && Object.keys(userDetails).length > 0) {
      getAllUserAttributesDefinition();
    }
  }, [userDetails]);

  useLayoutEffect(() => {
    const fetchData = async () => {
      try {
        setIsOptionsLoading((prev) => ({
          ...prev,
          organization_keys: true,
          user_type: true
        }));

        const organizationList: IAttributeDef[] = store
          .getState()
          .userDetails?.organizations?.map((organization: ILooseObject) => ({
            value: organization?.organization_key ?? '-',
            label: organization?.ui_label ?? '-'
          }));
        const userTypeList = await getAllUserTypeList();

        setInputOptions((prev) => ({
          ...prev,
          organization_keys: organizationList,
          user_type: userTypeList
        }));
      } catch (error) {
        enqueueSnackbar(JSON.stringify(error), {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } finally {
        setIsOptionsLoading((prev) => ({
          ...prev,
          organization_keys: false,
          user_type: false
        }));
      }
      // Start loading for both
    };
    if (getUserPermissionsUserPage()?.includes('user_view')) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUnSelectableRows((prev) => {
      const prevSet = new Set(Object.values(prev));
      const newSet = new Set(collectionDisabledRows);

      // Compare the previous and new sets to avoid unnecessary updates
      if (prevSet.size === newSet.size && [...prevSet].every((val) => newSet.has(val))) {
        return prev; // No change, avoid state update
      }
      return collectionDisabledRows;
    });
  }, [collectionDisabledRows]);

  useEffect(() => {
    // Add useEffect to populate application options from user details
    const allApplications: ILooseObject = {};
    // First add applications from userDetails
    if (userDetails?.organizations?.length > 0) {
      userDetails.organizations.forEach((organization: ILooseObject) => {
        if (organization?.applications?.length > 0) {
          organization.applications.forEach((application: ILooseObject) => {
            if (application?.application_key && application?.ui_label) {
              allApplications[application.application_key] = {
                label: application.ui_label,
                value: application.application_key
              };
            }
          });
        }
      });
    }

    const meApiApplicationsOptions = meApiData?.OptionsAndPermissions?.applicationsOptions;
    // Then add any remaining applications from commonAppKeys that weren't in userDetails
    commonAppKeys.forEach((appKey: string) => {
      const existingApp = Object.values(allApplications).find((app) => app.value === appKey);
      if (!existingApp) {
        // If app not found in userDetails, add it with the key as label
        const applicationOption: IAttributeOptions | undefined = meApiApplicationsOptions?.[appKey];
        if (applicationOption && applicationOption?.value) {
          allApplications[applicationOption?.value as string] = {
            label: applicationOption?.label,
            value: applicationOption?.value
          };
        }
      }
    });

    // Convert Set to Array and sort by label
    const sortedApplications = Object.values(allApplications).sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    setApplicationOptions(sortedApplications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, commonAppKeys]);

  useEffect(() => {
    // Set initial selected application when options are loaded
    if (applicationOptions.length > 0 && typeof applicationOptions[0].value === 'string') {
      setSelectedApplication(applicationOptions[0].value);
    }
  }, [applicationOptions]);

  useEffect(() => {
    // Update useEffect to fetch graph data when application changes
    if (
      selectedApplication &&
      tabSelectionValue === 1 &&
      appKeysWithPermissions?.[selectedApplication]?.includes('all_role_view')
    ) {
      getAllGraphRoles({ page: 0, size: -1 }, [selectedApplication]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication, tabSelectionValue]);

  if (!isAuthorized) {
    return <NoAuthorization />;
  }

  return (
    <Box>
      <DynamicBreadCrumbs
        breadCrumbsDetails={[
          { key: 1, name: 'All users', redirectPath: '/users' },
          { key: 2, name: 'User Details' }
        ]}
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" rowGap={2} mt={2}>
          {getUserPermissionsUserPage()?.includes('user_view') ? (
            <FormProvider {...formMethods}>
              <Box
                component="form"
                onSubmit={handleSubmitUserDetails(handleUpdateUserDetails)}
                border={1}
                borderColor={lightTheme.palette.grey[300]}
                borderRadius={3}
                sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  textTransform="none"
                  color="primary.dark"
                  textAlign="center"
                >
                  User details
                </Typography>
                <Grid2 container columnSpacing={10}>
                  {AttributesConfig.USER_DETAILS?.map((attribute: IAttributeDef) => {
                    const inputType = UI_TYPE_INPUT_TYPE_MAP[attribute?.ui_type?.toUpperCase()];
                    const options = attribute?.options ?? inputOptions?.[attribute?.name] ?? [];
                    const inputFieldType: INPUT_TYPES_ENUM =
                      INPUT_FIELD_TYPE_MAP[attribute?.value_type?.toUpperCase()];
                    const isDisabled = !attribute?.is_editable || userUiMode === 'VIEW';
                    const isVisible: boolean = !!attribute?.is_visible;
                    if (!isVisible) {
                      return null;
                    }

                    return (
                      <Grid2
                        container
                        size={{ xs: 6 }}
                        key={attribute?.attribute_id}
                        alignItems="center"
                      >
                        {/* UI label */}
                        <Grid2 size={{ xs: 4 }} display="flex" alignItems="center">
                          <Box display="flex" sx={{ textAlign: 'left', pb: 4 }}>
                            <Typography>{`${attribute?.ui_label} ${attribute?.is_mandatory ? '*' : ''}`}</Typography>
                          </Box>
                        </Grid2>
                        {/* DynamicInput */}
                        <Grid2 size={{ xs: 8 }}>
                          {isOptionsLoading?.[attribute?.name] ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <CircularProgress size={30} />
                            </Box>
                          ) : (
                            <Controller
                              name={attribute.name as string}
                              control={controlUserDetails}
                              render={({ field: { onChange, onBlur, value, name } }) => (
                                <DynamicInput
                                  type={inputType}
                                  id={attribute?.attribute_id}
                                  label={attribute?.ui_label}
                                  value={value}
                                  hasError={!!(errorsUserDetails?.[name]?.message as string)}
                                  helperText={(errorsUserDetails?.[name]?.message as string) ?? ' '}
                                  options={options}
                                  maxLength={attribute?.max_length}
                                  disabled={isDisabled}
                                  placeholder={attribute?.placeholder}
                                  disableClearable={attribute?.disable_clearable}
                                  onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    onChange(e);
                                    clearErrorsUserDetails(name);
                                  }}
                                  onBlur={onBlur}
                                  inputType={inputFieldType}
                                />
                              )}
                            />
                          )}
                        </Grid2>
                      </Grid2>
                    );
                  })}
                </Grid2>
                {/* Meta Data Section */}
                <Divider />
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  textAlign={'center'}
                  color="primary.dark"
                >
                  Attributes
                </Typography>
                {isUserAttributesDefinitionLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <UserMetaDataRow
                    userAttributesDefinition={userAttributesDefinition}
                    fields={metaFields}
                    append={appendMeta}
                    defaultMetaRow={defaultMetaRow}
                    remove={removeMeta}
                    control={controlUserDetails}
                    clearErrors={clearErrorsUserDetails}
                    errors={errorsUserDetails?.metadata ?? {}}
                    disabled={userUiMode === 'VIEW'}
                    setValue={setValueUserDetails}
                  />
                )}

                <Box display="flex" columnGap={1} justifyContent="flex-end">
                  {userUiMode === 'VIEW' ? (
                    <>
                      {getUserPermissionsUserPage()?.includes('user_delete') && (
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          loading={isSubmittingUserDetails}
                          onClick={() => setIsOpenDeleteDialog(true)}
                          color="error"
                          disabled={userDetails?.user_key === meApiData?.user_key}
                          sx={{ textTransform: 'none' }}
                          startIcon={<DeleteIcon />}
                        >
                          Delete user
                        </LoadingButton>
                      )}
                      {getUserPermissionsUserPage()?.includes('user_edit') && (
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          onClick={handleEditUserDetails}
                          loading={isSubmittingUserDetails}
                          sx={{ textTransform: 'none' }}
                          startIcon={<EditIcon />}
                        >
                          Edit user
                        </LoadingButton>
                      )}
                    </>
                  ) : (
                    getUserPermissionsUserPage()?.includes('user_edit') && (
                      <>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={handleResetUserDetails}
                        >
                          Cancel
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          type="submit"
                          loading={isSubmittingUserDetails}
                          disabled={
                            Object.keys(errorsUserDetails).length > 0 || !isDirtyUserDetails
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Save
                        </LoadingButton>
                      </>
                    )
                  )}
                </Box>
              </Box>
            </FormProvider>
          ) : (
            <NoAuthorization />
          )}
          <Box
            border={1}
            borderColor={lightTheme.palette.grey[300]}
            borderRadius={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
          >
            <Box>
              <Typography
                variant="body1"
                fontWeight="bold"
                textTransform="none"
                color="primary.dark"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap'
                }}
              >
                User assigned roles
              </Typography>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <GraphTableSwitcher
                  handleChangeTab={handleChangeTab}
                  selectedTabValue={tabSelectionValue}
                  disableTabs={applicationUiMode === 'EDIT'}
                />
                {/* Edit roles section */}
                {isEditEnabled && (
                  <Box display="flex" columnGap={1} justifyContent="flex-end">
                    {applicationUiMode === 'VIEW' ? (
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        onClick={handleEditRoles}
                        sx={{ textTransform: 'none' }}
                        startIcon={<EditIcon />}
                        disabled={
                          !selectedApplication ||
                          !meApiData?.OptionsAndPermissions?.appPermissions?.[
                            selectedApplication
                          ]?.includes('all_role_view')
                        }
                      >
                        Edit roles
                      </LoadingButton>
                    ) : (
                      <>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={handleResetApplications}
                        >
                          Cancel
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          onClick={(data) =>
                            tabSelectionValue === 1
                              ? handleSubmitRoles({ roles: currentAssignedGraphRoleIds })
                              : handleSubmitApplications(handleSubmitRoles)(data)
                          }
                          loading={isSubmittingApplications || isLoadingGraphRoleSubmission}
                          disabled={
                            !isDirtyApplications &&
                            JSON.stringify(initialAssignedGraphRoleIds) ===
                              JSON.stringify(currentAssignedGraphRoleIds)
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Save
                        </LoadingButton>
                      </>
                    )}
                  </Box>
                )}
              </Box>
              <Grid2 container sx={{ mt: 2, mb: 2 }}>
                <Grid2 size={{ xs: 4 }}>
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                    id={1}
                    label="Select Application"
                    value={selectedApplication}
                    options={applicationOptions}
                    onChange={handleApplicationChanged}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    hasError={false}
                    placeholder="Please select application"
                    disabled={applicationUiMode === 'EDIT'}
                  />
                </Grid2>
              </Grid2>
            </Box>
            {/* role tagging section */}
            {selectedApplication && (
              <>
                {tabSelectionValue === 0 ? (
                  <Box component="form" sx={{ mt: 2 }}>
                    <Controller
                      name="roles"
                      control={controlApplications}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <DataGrid
                            getRowId={(row) => row.role_key}
                            loading={isLoadingRoles}
                            rows={userAssignedRoleTable?.rows}
                            columns={userAssignedRoleTable.columns}
                            disableRowSelectionOnClick
                            disableColumnSelector
                            disableColumnMenu
                            disableDensitySelector
                            getRowHeight={() => 'auto'}
                            {...(applicationUiMode === 'EDIT' && {
                              checkboxSelection: true,
                              rowSelectionModel: value,
                              onRowSelectionModelChange: (newRowSelectionModel) => {
                                setRowSelectionModel(newRowSelectionModel?.toSorted());
                                onChange(newRowSelectionModel?.toSorted());
                              },
                              isRowSelectable: (params: GridRowParams) => {
                                return (
                                  !params?.row?.disabled &&
                                  !unSelectableRows?.includes(params?.row?.role_key)
                                );
                              }
                            })}
                            sx={{
                              '& .MuiDataGrid-columnHeaderTitle': {
                                textOverflow: 'clip',
                                whiteSpace: 'break-spaces',
                                lineHeight: 1.15,
                                fontWeight: 600
                              },
                              '& .MuiDataGrid-columnHeaderCheckbox': {
                                pointerEvents: 'none'
                              },
                              '& .MuiDataGrid-row': {
                                minHeight: '52px !important'
                              },
                              '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                                outline: 'none'
                              },
                              '& .MuiDataGrid-cell:focus': {
                                outline: 'none'
                              },
                              '& .MuiDataGrid-cell:focus-within': {
                                outline: 'none'
                              },
                              '& .MuiDataGrid-main': {
                                overflow: 'unset'
                              },
                              '& .MuiDataGrid-columnHeaders': {
                                position: 'sticky',
                                top: 63,
                                zIndex: 1
                              },
                              '& .MuiDataGrid-container--top': {
                                zIndex: 1
                              },
                              '& .MuiDataGrid-columnHeader': {
                                backgroundColor: lightTheme.palette.grey[100]
                              }
                            }}
                          />
                        );
                      }}
                    />
                    <Box
                      display="flex"
                      alignItems="center"
                      width="100%"
                      position="relative"
                      minHeight={50}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        textTransform="none"
                        color="primary.dark"
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        User derived roles
                      </Typography>

                      {/* Spacer to push buttons to the right */}
                      <Box sx={{ flexGrow: 1 }} />
                    </Box>
                    {/* role tagging section */}
                    <Box component="form" sx={{ mt: 2 }}>
                      <DataGrid
                        getRowId={(row) => row.role_key}
                        rows={userDerivedRoleTable?.rows}
                        columns={userDerivedRoleTable.columns}
                        disableRowSelectionOnClick
                        disableColumnSelector
                        disableColumnMenu
                        disableDensitySelector
                        getRowHeight={() => 'auto'}
                        sx={{
                          '& .MuiDataGrid-columnHeaderTitle': {
                            textOverflow: 'clip',
                            whiteSpace: 'break-spaces',
                            lineHeight: 1.15,
                            fontWeight: 600
                          },
                          '& .MuiDataGrid-row': {
                            minHeight: '52px !important'
                          },
                          '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                            outline: 'none'
                          },
                          '& .MuiDataGrid-cell:focus': {
                            outline: 'none'
                          },
                          '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none'
                          },
                          '& .MuiDataGrid-main': {
                            overflow: 'unset'
                          },
                          '& .MuiDataGrid-columnHeaders': {
                            position: 'sticky',
                            top: 63,
                            zIndex: 1
                          },
                          '& .MuiDataGrid-container--top': {
                            zIndex: 1
                          },
                          '& .MuiDataGrid-columnHeader': {
                            backgroundColor: lightTheme.palette.grey[100]
                          }
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, position: 'relative' }}>
                    {isGraphLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%'
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : appKeysWithPermissions?.[selectedApplication]?.includes('all_role_view') ? (
                      <DirectiveGraph
                        initialNodes={graphData.nodes}
                        initialEdges={graphData.edges}
                        backgroundVariant={BackgroundVariant.Dots}
                        backgroundColor={applicationUiMode === 'VIEW' ? 'rgb(13, 12, 12)' : ''}
                        onNodeClick={handleNodeClick}
                        isDisableNode={applicationUiMode === 'VIEW'}
                      />
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <NoAuthorization />
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
      <ConfirmDialog
        content={`Are you sure you want to delete ${userDetails?.name} ?`}
        handleCloseAction={() => setIsOpenDeleteDialog(false)}
        handleSaveAction={() => handleDeleteUser(userDetails?.user_key)}
        open={isOpenDeleteDialog}
        isLoadingSave={isLoadingDeleteUser}
        saveActionName="Confirm"
      />
    </Box>
  );
};

export default UserDetails;
